<template>
  <ui-dialog
    :show="isOpen"
    persistent
    scrollable
    max-width="623"
    @input="close"
  >
    <template #header>
      <h3 v-if="chosenCategoryType">
        {{
          $t(`message.facilityDetails.categories.${chosenCategoryType}.title`)
        }}
      </h3>
      <v-text-field
        v-if="chosenCategoryType"
        v-model="categoriesSearchInputValue"
        :placeholder="
          $t(
            `message.facilityDetails.categories.${chosenCategoryType}.queryPlaceholder`
          )
        "
        prepend-inner-icon="icon-search"
        clearable
        outlined
        rounded
        class="mt-4 mb-4"
        :hide-details="true"
        @input="onCategorySearch"
        ref="input"
      ></v-text-field>
      <v-row
        no-gutters
        v-if="currentPageCategories.length"
        class="align-center mb-1"
      >
        <v-col>
          <Pagination
            class="flex-grow-1"
            :page="page"
            :per-page="perPage"
            :options="perPageOptions"
            :total-count="totalCount"
            @change="handleChange"
          ></Pagination>
        </v-col>
      </v-row>
    </template>
    <template #default>
      <div class="add-category">
        <v-radio-group
          v-if="currentPageCategories.length"
          class="mt-0"
          v-model="chosenCategory"
          color="primary"
          row
        >
          <v-radio
            v-for="item in currentPageCategories"
            class="list-item"
            :key="item.id"
            :value="item"
            :disabled="addedCategoriesIds.includes(item.id)"
          >
            <template v-slot:label>
              <div class="list-item__label">
                <div v-if="showOriginalName" class="list-item__label-secondary">
                  {{ item.name_en }}
                </div>
                <div>{{ item.name }}</div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
        <no-data v-else></no-data>
      </div>
      <SectionLoader :status="loadingList" />
    </template>
    <template #actions>
      <ui-button outlined @click="close">
        {{ $t("message.common.close") }}
      </ui-button>
      <ui-button
        v-if="chosenCategoryType"
        color="primary"
        @click="save"
        :disabled="!chosenCategory"
      >
        {{
          $t(
            `message.facilityDetails.categories.${chosenCategoryType}.applyBtnText`
          )
        }}
      </ui-button>
    </template>
  </ui-dialog>
</template>

<script>
import { CategoryType } from "@/enums/category-type";
import { debounce } from "@/helpers/helpers";
import serviceHandler from "@/helpers/handlers/service";
import businessAddressesService from "@/services/businessAddresses";
import { mapState } from "vuex";
import { LOCALE } from "@/enums/locale";

import Pagination from "@/components/List/Pagination";
import SectionLoader from "@/components/Loader/SectionLoader";
import NoData from "@/components/ui/NoData";

export default {
  name: "AddCategoryDialog",
  components: {
    Pagination,
    SectionLoader,
    NoData
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    addedCategoriesIds: {
      type: Array,
      default: () => []
    },
    chosenCategoryType: {
      type: String,
      validator: value =>
        value === CategoryType.PRIMARY || value === CategoryType.ADDITIONAL
    }
  },
  data() {
    return {
      page: 1,
      perPage: null,
      perPageOptions: [10, 50, 100, 200],
      categoriesSearchInputValue: "",
      loading: false,
      loadingList: false,
      totalCount: 0,
      currentPageCategories: [],
      chosenCategory: null
    };
  },
  watch: {
    async isOpen(newValue) {
      if (newValue) {
        this.loadingList = true;
        await this.fetchCategories();
        this.loadingList = false;

        setTimeout(() => {
          this.$refs.input.focus();
        }, 150);
      } else {
        this.currentPageCategories = [];
        this.chosenCategory = null;
        this.page = 1;
        this.perPage = this.perPageOptions[1];
        this.categoriesSearchInputValue = "";
      }
    }
  },
  created() {
    this.perPage = this.perPageOptions[1];
  },
  methods: {
    close() {
      this.$emit("close");
    },

    onCategorySearch: debounce(async function() {
      this.loadingList = true;
      this.page = 1;
      await this.fetchCategories();
      this.loadingList = false;
    }, 500),

    async fetchCategories() {
      const params = {
        page: this.page,
        perPage: this.perPage,
        query: this.categoriesSearchInputValue
      };

      const res = await serviceHandler(
        businessAddressesService.getCategoriesList(params)
      );

      this.totalCount = res.count;
      this.currentPageCategories = res.results;
    },

    async handleChange({ page, perPage }) {
      this.page = page;
      this.perPage = perPage;
      this.loadingList = true;
      await this.fetchCategories();
      this.loadingList = false;
    },

    save() {
      this.$emit("add", { category: this.chosenCategory });
      this.close();
    }
  },
  computed: {
    ...mapState({
      selectedLocale: state => state.settings.selectedLocale
    }),
    showOriginalName() {
      return this.selectedLocale !== LOCALE.EN;
    }
  }
};
</script>

<style lang="scss">
.add-category {
  margin-bottom: $spacing * 2;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .list-item {
    display: flex;
    justify-content: space-between;
    margin-right: 0 !important;
    padding: $spacing * 1.75 0;
    padding-right: $spacing * 4;
    flex-direction: row-reverse;
    width: 100%;

    &:not(:last-child) {
      border-bottom: 1px solid $light-3;
    }

    &__label {
      display: flex;
      flex-direction: column;
    }

    &__label-secondary {
      @include font-size(10px);
      color: $gray;
    }
  }

  .v-radio--is-disabled > * {
    opacity: 0.5;
  }
}
</style>

<style lang="scss" scoped>
.v-label {
  padding-right: $spacing;
}

.dialog-header {
  margin-right: 0 !important;
}

.v-card__title {
  padding-bottom: $spacing * 3;

  h3 {
    line-height: 36px;
  }
}
</style>
